import dayjs from "../utils/dayjs";
import i18n from "../i18n";

export const stringSeprator = (str, count) => {
    let str1 = str?.length > count ? str?.slice(0, count) + " ..." : str;
    return str1;
}
export const objToQueryParam = (obj) => {
    return new URLSearchParams(
        Object.entries(obj).reduce((acc, [key, value]) => {
            if ((typeof value === 'string' && value.trim()) || (typeof value === 'number' && value > 0) || (typeof value === 'boolean' && value == true)) {
                acc[key] = value;
            }
            return acc;
        }, {})
    );
}
export const queryParamToObj = (params) => {
    return Object.fromEntries(new URLSearchParams(params).entries());
}
export const imagePath = (path, dummy) => {
    if (!path) return dummy || "/assets/img/placeholder.png";

    const apiUrl = process.env.REACT_APP_API_URL
        ? process.env.REACT_APP_API_URL.replace(/\/$/, "")
        : "";

    if (typeof path === "string") {
        const normalized = path.replace(/(https?:\/\/(?:localhost|127\.0\.0\.1)):(6090)(?=\/|$)/, "$1:6089");
        if (normalized.startsWith("http://") || normalized.startsWith("https://")) {
            return normalized;
        }
        // Fichiers servis par l'API : un chemin relatif `/img/…` ou `/uploads/…`
        // doit pointer vers le domaine API (sinon, en app native Capacitor, il
        // résout contre `capacitor://localhost` → « Cannot GET /img/… »).
        if (normalized.startsWith("/img/") || normalized.startsWith("/uploads/")) {
            return apiUrl ? `${apiUrl}${normalized}` : normalized;
        }
        if (normalized.startsWith("/")) {
            return normalized;
        }
        if (normalized.startsWith("assets/")) {
            return `/${normalized}`;
        }
        const relative = normalized.startsWith("img/") ? normalized : `img/${normalized}`;
        return apiUrl ? `${apiUrl}/${relative}` : `/${relative}`;
    }

    return dummy || "/assets/img/placeholder.png";
}
// Image optimisée : les uploads servis par l'API /img/ passent par le resize
// serveur (WebP) — indispensable pour les photos 4000px sur mobile.
export const optimizedImage = (img, width = 1200) => {
    const src = imagePath(img);
    if (typeof src === "string" && src.includes("/img/") && !src.includes("?")) {
        return `${src}?w=${width}&q=80`;
    }
    return src;
};

export const capLetter = (value) => {
    return value?.charAt(0)?.toUpperCase() + value?.slice(1)?.toLowerCase();
}

// Capitalise la 1re lettre de chaque mot (« morgan coue - iad france » → « Morgan Coue - Iad France »).
export const titleCase = (value) =>
    String(value || "").replace(/(^|[\s\-'’(])(\p{L})/gu, (m, sep, ch) => sep + ch.toUpperCase());
export const translatePropertyType = (value) => {
    const v = String(value ?? "").trim();
    if (!v) return v;
    const keyMap = {
        apartment: "home.propertyTypes.apartment",
        appartement: "home.propertyTypes.apartment",
        house: "home.propertyTypes.house",
        maison: "home.propertyTypes.house",
        land: "home.propertyTypes.land",
        terrain: "home.propertyTypes.land",
        castle: "home.propertyTypes.castle",
        chateau: "home.propertyTypes.castle",
        building: "home.propertyTypes.building",
        immeuble: "home.propertyTypes.building",
        farm: "home.propertyTypes.farm",
        ferme: "home.propertyTypes.farm",
    };
    const key = keyMap[v.toLowerCase()];
    if (!key) return v;
    const translated = i18n.t(key);
    return translated && translated !== key ? translated : v;
};

export const generateDynamicString = (obj) => {
    const formatted = [];
    for (const [key, value] of Object.entries(obj)) {
        if (
            value !== null &&
            value !== undefined &&
            (typeof value !== "string" || value.trim() !== "") &&  // Exclude empty strings and strings with only spaces
            value !== 0 &&
            !(Array.isArray(value) && value.length === 0)
        ) {
            switch (key) {
                case "propertyType":
                    const ptLabel =
                        value === "sale" ? i18n.t("propertyTypes.buy")
                            : value === "rent" ? i18n.t("propertyTypes.rent")
                                : value === "directory" ? i18n.t("home.tabs.directory")
                                    : value === "offmarket" ? i18n.t("propertyTypes.offMarket")
                                        : capLetter(value);
                    formatted.push(ptLabel);
                    break;
                case "type":
                    // `type` peut contenir plusieurs valeurs séparées par des
                    // virgules (ex. "Apartment,House") : traduire chaque valeur
                    // séparément, sinon la chaîne entière reste en anglais.
                    formatted.push(
                        String(value)
                            .split(",")
                            .map((v) => translatePropertyType(v.trim()))
                            .filter(Boolean)
                            .join(", ")
                    );
                    break;
                case "search":
                    // Plusieurs localisations sont séparées par « / » (cohérent avec
                    // le backend). On n'affiche « (+N) » que s'il y a réellement
                    // plusieurs localisations.
                    const searchParts = String(value).split(" / ").map((s) => s.trim()).filter(Boolean);
                    if (searchParts.length > 1) {
                        formatted.push(`${stringSeprator(capLetter(searchParts[0]), 20)} (+${searchParts.length - 1})`);
                        break;
                    }
                    formatted.push(capLetter(searchParts[0] || value));
                    break;
                case "minPrice":
                    formatted.push(i18n.t("filtersCommon.resultMinPrice", { value: `${formatCurrency(value)} €` }));
                    break;
                case "maxPrice":
                    formatted.push(i18n.t("filtersCommon.resultMaxPrice", { value: `${formatCurrency(value)} €` }));
                    break;
                case "proposal":
                    const proposalLabel =
                        value === "purchase" ? i18n.t("filtersCommon.resultProposalPurchase")
                            : value === "rental" ? i18n.t("filtersCommon.resultProposalRental")
                                : `${value} proposal`;
                    formatted.push(proposalLabel);
                    break;
                case "minRevenues":
                    formatted.push(i18n.t("filtersCommon.resultMinRevenues", { value: `${formatCurrency(value)} €` }));
                    break;
                case "maxRevenues":
                    formatted.push(i18n.t("filtersCommon.resultMaxRevenues", { value: `${formatCurrency(value)} €` }));
                    break;
                case "minSurface":
                    formatted.push(i18n.t("filtersCommon.resultMinSurface", { value: `${formatCurrency(value)} m2` }));
                    break;
                case "maxSurface":
                    formatted.push(i18n.t("filtersCommon.resultMaxSurface", { value: `${formatCurrency(value)} m2` }));
                    break;
                case "rooms":
                    formatted.push(i18n.t("filtersCommon.resultRooms", { count: value }));
                    break;
                case "rating":
                    formatted.push(i18n.t("filtersCommon.resultRating", { count: value }));
                    break;
                default:
                    break;
            }
        }
    }
    return formatted.join(", ");
}
export const dateFormate = (date, format = "DD/MM/YYYY") => {
    try {
        const lang = i18n.language || 'fr';
        dayjs.locale(lang.startsWith('fr') ? 'fr' : 'en');
    } catch (e) {}
    const d = dayjs(date);
    return d.isValid() ? d.format(format) : "Invalid date";
}
export const addLocal = (key, value) => {
    return localStorage.setItemItem(key, value)
}
export const removeLocal = (key) => {
    return localStorage.removeItem(key)
}
export const removePropData = (preserveSelectedType = false) => {
    const keysToRemove = [
        "step1",
        "addMore",
    ];
    if (!preserveSelectedType) {
        keysToRemove.push("step1");
    }
    keysToRemove?.forEach(key => localStorage.removeItem(key));
};
export const cleanObject = (inputObj) => {
    return Object.fromEntries(
        Object.entries(inputObj).filter(([_, value]) => value !== "")
    );
};
export const formatCurrency = (value) => {
    if (value === undefined || value === null || value === "") return "";
    let number = value?.toString()?.replace(/\D/g, "");
    if (!number) return "";
    return number.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
}

// Surface : conserve les décimales (ex. 21.6 → « 21,6 »), contrairement à
// formatCurrency qui supprime tout caractère non numérique.
export const formatSurface = (value) => {
    if (value === undefined || value === null || value === "") return "";
    const n = Number(String(value).replace(",", "."));
    if (!Number.isFinite(n)) return String(value);
    return n.toLocaleString("fr-FR", { maximumFractionDigits: 2 });
}
export const removeHTMLTags = (str) => {
    return str?.replace(/<\/?[^>]+(>|$)/g, "")?.trim()?.replace("&nbsp;", " ");
}
export const getOrdinal = (number) => {
    if (typeof number !== "number" || isNaN(number) || !Number.isInteger(number) || number < 0) {
        throw number || "";
    }
    const lastDigit = number % 10;
    const lastTwoDigits = number % 100;
    if (lastTwoDigits >= 11 && lastTwoDigits <= 13) {
        return `${number}th`; // Special case for 11th, 12th, 13th
    }
    switch (lastDigit) {
        case 1:
            return `${number}st`;
        case 2:
            return `${number}nd`;
        case 3:
            return `${number}rd`;
        default:
            return `${number}th`;
    }
}
export const downloadFile = (path) => {
    const url = imagePath(path);
    window.open(url, "_blank");
}

/**
 * Privacy-safe owner display name.
 * - Pro  : companyName → fullName → firstName + lastName
 * - Other: username → firstName + first letter of lastName
 */
export const getOwnerDisplayName = (user) => {
  if (!user) return '';
  if (user.accountType === 'pro') {
    if (user.companyName?.trim()) return user.companyName.trim();
    if (user.fullName?.trim()) return user.fullName.trim();
    return `${user.firstName || ''} ${user.lastName || ''}`.trim();
  }
  if (user.username?.trim()) return user.username.trim();
  const first = (user.firstName || '').trim();
  const lastInitial = user.lastName?.trim()
    ? user.lastName.trim().charAt(0).toUpperCase() + '.'
    : '';
  return `${first}${lastInitial ? ' ' + lastInitial : ''}`.trim();
};